import { BarChart } from '@mui/x-charts/BarChart';
import { useBizTypeTranslation } from "hooks/useBizTypeTranslation";
import { IconButton, Stack, Typography, useTheme } from "@mui/material"
import { useEffect, useState } from 'react';
import moment from 'moment';
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import NullCheck from 'components/NullCheck';
import { API_DATE_FORMAT, DATE_FOR_API_FORMAT, MC_SOFTWARE_TYPE } from 'config/const';

const ChartHoursAvg = ({ data, getDateRanges, setGridDataList, padding, softwareType }) => {
  const theme = useTheme();
  const { bt } = useBizTypeTranslation();
  const [daysList, setDaysList] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayFormat, setDayFormat] = useState(null);
  const [list, setList] = useState([]);

  useEffect(() => {
    if (data) {
      const days = getDateRanges(moment(data?.dates?.start), moment(data?.dates?.end), 'day')
      setDaysList(days)
      setSelectedDay(days[0]);
    }
  }, [data]);

  useEffect(() => {
    if (selectedDay && data) {
      const hourlyData = prepareHourlyTempDataForDate(data?.avgs, selectedDay.startDate);
      setList(hourlyData);
      const [month, day, year] = selectedDay?.startDate?.split("-");
      setDayFormat(`${day}.${month}.${year}`)
    }
  }, [selectedDay, data]);

  const onSelectedDayChange = (type) => {
    if (type === "next") {
      const currentIndex = daysList.findIndex(day => day.startDate === selectedDay.startDate);
      if (currentIndex < daysList.length - 1) {
        setSelectedDay(daysList[currentIndex + 1]);
      }
    }
    else if (type === "prev") {
      const currentIndex = daysList.findIndex(day => day.startDate === selectedDay.startDate);
      if (currentIndex > 0) {
        setSelectedDay(daysList[currentIndex - 1]);
      }
    }
  };

  function prepareHourlyTempDataForDate(data, dateStr) {
    const x = [];
    const y1 = [];
    const y2 = [];

    const hourlyMap = {};

    data.forEach((item) => {
      const created = moment(item.created);
      const itemDate = created.format(DATE_FOR_API_FORMAT);

      if (itemDate !== dateStr) return;

      const hour = created.startOf('hour').format('HH:00');

      if (!hourlyMap[hour]) {
        hourlyMap[hour] = {
          temp1: item.temp1 ?? 0,
          temp2: item.temp2 ?? 0,
        };
      }
    });

    let newGridDataList = []

    for (let i = 0; i < 24; i++) {
      const hour = moment().startOf('day').add(i, 'hours').format('HH:00');
      x.push(hour);

      const temps = hourlyMap[hour] || { temp1: 0, temp2: 0 };
      y1.push(temps.temp1);
      y2.push(temps.temp2);

      newGridDataList?.push({ id: i + 1, hour: hour, temp1: temps.temp1, temp2: temps.temp2 })
    }

    setGridDataList(newGridDataList)
    return { x, y1, y2 };
  }


  return (
    <Stack width='100%' justifyContent='center' padding={padding ? padding : 1}>

      <Stack
        paddingX={1}
        width='100%'
        direction={{ xs: "column-reverse", md: "row" }}
        justifyContent='space-between'
        alignItems={'center'}
      >

        <NullCheck valueForCheck={daysList?.length > 1} nullToDiv>
          <Stack direction='row' alignItems='center' spacing={1}>

            <IconButton
              disabled={selectedDay === daysList[0]}
              onClick={() => onSelectedDayChange('prev')}
              size='small'
            >
              <ArrowForwardIos fontSize='small' />
            </IconButton>

            <Typography>{dayFormat}</Typography>

            <IconButton
              onClick={() => onSelectedDayChange('next')}
              size='small'
              disabled={selectedDay === daysList[daysList?.length - 1]}
            >
              <ArrowBackIos fontSize='small' />
            </IconButton>
            <Stack />
          </Stack>
        </NullCheck>

        <Typography variant="h6" textAlign="center">
          {moment(data?.dates?.start).format(API_DATE_FORMAT)} - {moment(data?.dates?.end).format(API_DATE_FORMAT)}
        </Typography>

      </Stack>

      <NullCheck valueForCheck={list?.x?.length > 0}>
        <BarChart
          margin={{ top: 30, right: 5, bottom: 30, left: 30 }}
          xAxis={[{ data: list?.x, scaleType: 'band' }]}
          series={list?.y2?.some(t => t > 0)
            ? [
              {
                data: list?.y1,
                label: bt(softwareType === MC_SOFTWARE_TYPE.B1_9 ? "WATER_TEMP1" : "TEMP1"),
                valueFormatter: (value) => `${value}°`,
                color: theme.palette.primary.main
              },
              {
                data: list?.y2,
                label: bt(softwareType === MC_SOFTWARE_TYPE.B1_9 ? "WATER_TEMP2" : "TEMP2"),
                valueFormatter: (value) => `${value}°`,
                color: theme.palette.primary.text
              },
            ]
            : [{ data: list?.y1, label: bt('TEMP'), valueFormatter: (value) => `${value}°`, color: theme.palette.primary.main },]
          }
          height={300}
          slotProps={{ legend: { hidden: true } }}
        />
      </NullCheck>
    </Stack>
  )
}
export default ChartHoursAvg